import { c as _c } from "react/compiler-runtime";
import { IcoEmail24, IcoFaceid24, IcoSmartphone24 } from '@onefootprint/icons';
import type { AuthMethod } from '@onefootprint/request-types';
import { Button, PageShell } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import type { DeviceInfo } from '@/idv2/utils/get-device';
import { EditButton } from './edit-button';
type DashboardProps = {
  methods: AuthMethod[];
  decryptedEmail?: string;
  decryptedPhone?: string;
  deviceInfo: DeviceInfo;
  isInIframe: boolean;
  isLoading?: boolean;
  isEditing?: boolean;
  onEmailClick: () => void;
  onPhoneClick: () => void;
  onPasskeyClick: () => void;
  onDone: () => void;
};
const getMethodEntry = (methods: AuthMethod[], kind: string): AuthMethod | undefined => {
  return methods.find(m => m.kind === kind && m.canUpdate);
};
export const Dashboard = t0 => {
  const $ = _c(49);
  const {
    methods,
    decryptedEmail,
    decryptedPhone,
    deviceInfo,
    isInIframe,
    isLoading: t1,
    isEditing: t2,
    onEmailClick,
    onPhoneClick,
    onPasskeyClick,
    onDone
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const isEditing = t2 === undefined ? true : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keyPrefix: "authMethods"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t3);
  let t4;
  if ($[1] !== methods) {
    t4 = getMethodEntry(methods, "email");
    $[1] = methods;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const emailEntry = t4;
  let t5;
  if ($[3] !== methods) {
    t5 = getMethodEntry(methods, "phone");
    $[3] = methods;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const phoneEntry = t5;
  let t6;
  if ($[5] !== methods) {
    t6 = getMethodEntry(methods, "passkey");
    $[5] = methods;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const passkeyEntry = t6;
  let t7;
  if ($[7] !== isEditing || $[8] !== t) {
    t7 = isEditing ? t(_temp) : t(_temp2);
    $[7] = isEditing;
    $[8] = t;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const title = t7;
  let t8;
  if ($[10] !== isEditing || $[11] !== t) {
    t8 = isEditing ? t(_temp3) : t(_temp4);
    $[10] = isEditing;
    $[11] = t;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const subtitle = t8;
  let t9;
  if ($[13] !== decryptedEmail || $[14] !== emailEntry || $[15] !== isLoading || $[16] !== onEmailClick || $[17] !== t) {
    t9 = emailEntry && <EditButton icon={IcoEmail24} label={decryptedEmail || t(_temp5)} onClick={onEmailClick} isLoading={isLoading} isVerified={emailEntry.isVerified} isEmpty={!decryptedEmail} texts={{
      add: t(_temp6),
      edit: emailEntry.isVerified ? t(_temp7) : t(_temp8),
      verified: t(_temp9)
    }} />;
    $[13] = decryptedEmail;
    $[14] = emailEntry;
    $[15] = isLoading;
    $[16] = onEmailClick;
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== decryptedPhone || $[20] !== isLoading || $[21] !== onPhoneClick || $[22] !== phoneEntry || $[23] !== t) {
    t10 = phoneEntry && <EditButton icon={IcoSmartphone24} label={decryptedPhone || t(_temp10)} onClick={onPhoneClick} isLoading={isLoading} isVerified={phoneEntry.isVerified} isEmpty={!decryptedPhone} texts={{
      add: t(_temp11),
      edit: phoneEntry.isVerified ? t(_temp12) : t(_temp13),
      verified: t(_temp14)
    }} />;
    $[19] = decryptedPhone;
    $[20] = isLoading;
    $[21] = onPhoneClick;
    $[22] = phoneEntry;
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== deviceInfo || $[26] !== isInIframe || $[27] !== isLoading || $[28] !== onPasskeyClick || $[29] !== passkeyEntry || $[30] !== t) {
    t11 = passkeyEntry && deviceInfo.hasSupportForWebauthn && <EditButton icon={IcoFaceid24} label={t(_temp15)} onClick={onPasskeyClick} isLoading={isLoading} isDisabled={isInIframe} isVerified={passkeyEntry.isVerified} isEmpty={!passkeyEntry.isVerified} texts={{
      add: t(_temp16),
      edit: t(_temp17),
      verified: t(_temp18)
    }} />;
    $[25] = deviceInfo;
    $[26] = isInIframe;
    $[27] = isLoading;
    $[28] = onPasskeyClick;
    $[29] = passkeyEntry;
    $[30] = t;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t9) {
    t12 = <div className="flex flex-col gap-2 mb-6">{t9}{t10}{t11}</div>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t9;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const t13 = isEditing ? "primary" : "secondary";
  let t14;
  if ($[36] !== isEditing || $[37] !== t) {
    t14 = isEditing ? t(_temp19) : t(_temp20);
    $[36] = isEditing;
    $[37] = t;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== isLoading || $[40] !== onDone || $[41] !== t13 || $[42] !== t14) {
    t15 = <Button type="button" size="large" fullWidth={true} loading={isLoading} onClick={onDone} variant={t13}>{t14}</Button>;
    $[39] = isLoading;
    $[40] = onDone;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== subtitle || $[45] !== t12 || $[46] !== t15 || $[47] !== title) {
    t16 = <PageShell title={title} subtitle={subtitle}>{t12}{t15}</PageShell>;
    $[44] = subtitle;
    $[45] = t12;
    $[46] = t15;
    $[47] = title;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  return t16;
};
function _temp($) {
  return $.editTitle;
}
function _temp2($_0) {
  return $_0.title;
}
function _temp3($_1) {
  return $_1.editSubtitle;
}
function _temp4($_2) {
  return $_2.subtitle;
}
function _temp5($_3) {
  return $_3.email;
}
function _temp6($_4) {
  return $_4.add;
}
function _temp7($_5) {
  return $_5.replace;
}
function _temp8($_6) {
  return $_6.edit;
}
function _temp9($_7) {
  return $_7.verified;
}
function _temp10($_8) {
  return $_8.phoneNumber;
}
function _temp11($_9) {
  return $_9.add;
}
function _temp12($_10) {
  return $_10.replace;
}
function _temp13($_11) {
  return $_11.edit;
}
function _temp14($_12) {
  return $_12.verified;
}
function _temp15($_13) {
  return $_13.passkey;
}
function _temp16($_14) {
  return $_14.add;
}
function _temp17($_15) {
  return $_15.replace;
}
function _temp18($_16) {
  return $_16.added;
}
function _temp19($_17) {
  return $_17.done;
}
function _temp20($_18) {
  return $_18.skipAndFinish;
}